import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DropdownItem } from "reactstrap";
import { Button } from "../Button";
import routes from "@const/routes";
import { useTranslation } from "react-i18next";

import classNames from "classnames";
import { FormikProvider, useFormik } from "formik";

import Avatar from "../Avatar";
import FormikChatInput from "../FormikChatInput";
import Dropdown from "../Dropdown";
import CreatorsBadges from "../CreatorsBadges";
import VerifiedUserIcon from "../VerifiedUserIcon";
import { SkeletonCircle, SkeletonText } from "../Skeletons";
import { postDateFormat } from "@const/formats";
import { useService } from "@/base/hooks/useService";
import { StorageService } from "@services/StorageService";
import { localstorageKeys } from "@const/store";
import formatWithLocale from "@helpers/formatWithLocale";
import {
  deleteComment,
  editComment,
  likeComment,
  resetEditCommentId,
  setEditCommentId,
  unlikeComment,
} from "@redux/comments/actions";
import { MAX_COMMENT_LENGTH } from "@validation/lengthConstants";
import { validationSchema } from "./form";
import { ReactComponent as IconLike } from "../../assets/ic-like.svg";
import { ReactComponent as IconLiked } from "../../assets/ic-like-active.svg";
import { ReactComponent as IconShowMore } from "../../assets/ic-show-more.svg";

import styles from "./index.module.scss";
import { toggleSignInModal } from "@redux/signIn/actions";

export const CommentSkeleton = () => (
  <div className={classNames("d-flex", styles.comment)}>
    <SkeletonCircle className={classNames("bg-dark-gray", styles.avatar)} />
    <div className="w-100">
      <SkeletonText
        lines={1}
        lineClassName={classNames("bg-dark-gray", styles.title)}
      />
      <SkeletonText
        lines={2}
        lineClassName={classNames("bg-dark-gray", styles.text)}
      />
      <SkeletonText
        lines={1}
        lineClassName={classNames("bg-dark-gray", styles.text, styles.lastLine)}
      />
      <SkeletonText
        lines={1}
        lineClassName={classNames("bg-dark-gray", styles.date)}
      />
    </div>
  </div>
);

const Comment = ({ commentId, myUsername, isMyPost, postUniqueKey }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("buttons");

  const {
    text,
    createdAt,
    author,
    likesCount,
    isLiked,
    id,
    isLikeStatusChanging,
    isDeleting,
    isUpdating,
    isSupporter,
    isMostProfitableSupporter,
    isPromoter,
    isMostProfitablePromoter,
  } = useSelector(
    ({ commentsReducer }) => commentsReducer.commentById?.[commentId],
  );

  const storageService = useService(StorageService);
  const theme = storageService.get(localstorageKeys.theme, "");
  const { accessToken } = storageService.get(localstorageKeys.session, {});
  const { editCommentId } = useSelector(
    ({ commentsReducer }) => commentsReducer,
  );

  const { isDeleted, avatar, username, isIdentityVerified } =
    useSelector(({ authorsReducer }) => authorsReducer?.[author]) || {};

  const handleSubmit = ({ message }) => {
    dispatch(editComment({ id, text: message?.trim() }));
    resetEdit();
  };

  const formik = useFormik({
    validationSchema,
    initialValues: { message: text },
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const isMyComment = username === myUsername;

  const setEdit = () => dispatch(setEditCommentId(id));
  const resetEdit = () => {
    dispatch(resetEditCommentId());
    formik.resetForm();
  };

  useEffect(() => {
    formik.resetForm();
  }, [editCommentId]);

  const handleDelete = () => dispatch(deleteComment({ id }));

  const { pathname } = useLocation();

  const handleLike = () => {
    if (!accessToken) {
      dispatch(toggleSignInModal(true));
      storageService.set(
        localstorageKeys.destination,
        `${pathname}?view=${postUniqueKey}`,
      );
      return;
    }
    dispatch(isLiked ? unlikeComment({ id }) : likeComment({ id }));
  };

  if (isUpdating || !id) {
    return <CommentSkeleton />;
  }

  return (
    <div className={classNames("d-flex", styles.comment)}>
      <Avatar className={styles.avatar} avatar={avatar} isDeleted={isDeleted} />
      {editCommentId === id ? (
        <FormikProvider value={formik}>
          <form
            className="d-flex flex-column w-100"
            onSubmit={formik.handleSubmit}
          >
            <div
              className={classNames(
                "d-flex align-items-center",
                styles.formContent,
              )}
            >
              <FormikChatInput
                max={MAX_COMMENT_LENGTH}
                emojiClassName={styles.emoji}
                textareaClassName={classNames(styles.textarea, "ps-0")}
                rows={3}
                name="message"
                className="w-100"
                minHeight={80}
              />
            </div>
            <div className={classNames(styles.buttons, "d-flex ms-auto")}>
              <Button
                onClick={resetEdit}
                color="initial"
                className={classNames(
                  "btn-resetStyle",
                  theme === "light" ? styles.cancelLight : styles.cancelDark,
                )}
                type="button"
              >
                <span className="text-body-2 text-medium">{t("CANCEL")}</span>
              </Button>
              <Button
                type="submit"
                className={classNames("p-0 btn-resetStyle", styles.save)}
                disabled={!formik.isValid}
                color="initial"
              >
                <span className={classNames("text-body-2 text-medium")}>
                  {t("SAVE")}
                </span>
              </Button>
            </div>
          </form>
        </FormikProvider>
      ) : (
        <div className={classNames("w-100", styles.truncateWrapper)}>
          <div className="d-flex align-items-center mb-1">
            <div
              className={classNames(
                "d-flex align-items-center flex-1",
                styles.truncateWrapper,
              )}
            >
              <div
                className={classNames(
                  "d-flex align-items-center flex-1",
                  styles.truncateWrapper,
                )}
              >
                <Link
                  to={`${routes.CREATOR_PROFILE()}${username}`}
                  className={classNames(
                    "notranslate d-block me-2 text-body-1 text-decoration-none text-primary text-truncate text-medium",
                    styles.userName,
                    isDeleted && "pointer-events-none",
                  )}
                >
                  {username}
                </Link>
                <VerifiedUserIcon
                  isVisible={isIdentityVerified}
                  className="me-2"
                />
                <CreatorsBadges
                  isSupporter={isSupporter}
                  isTopSupporter={isMostProfitableSupporter}
                  isPromoter={isPromoter}
                  isTopPromoter={isMostProfitablePromoter}
                />
              </div>
            </div>
            <Dropdown
              end
              flip
              disabled={isDeleting}
              toggler={<IconShowMore />}
              togglerClassName={classNames(
                "btn-resetStyle",
                styles.dropdownToggler,
              )}
              wrapperClassName={classNames(
                !isMyPost && !isMyComment && "d-none",
                styles.dropdown,
              )}
            >
              <DropdownItem
                disabled={isDeleting}
                onClick={setEdit}
                className={classNames(!isMyComment && "d-none")}
              >
                {t("EDIT")}
              </DropdownItem>
              <DropdownItem
                disabled={isDeleting}
                onClick={handleDelete}
                className={classNames(!isMyPost && !isMyComment && "d-none")}
              >
                {t("DELETE")}
              </DropdownItem>
            </Dropdown>
          </div>
          <div className="text-primary mb-2 text-body-2 text-break text-emoji-regular">
            {text}
          </div>
          <div className="d-flex text-body-2 text-regular text-secondary">
            <div className="me-3 d-flex align-items-center">
              <button
                className={classNames("btn-resetStyle", styles.like)}
                onClick={handleLike}
                disabled={isLikeStatusChanging || isDeleting}
              >
                {isLiked ? (
                  <IconLiked className="me-1" />
                ) : (
                  <IconLike className="me-1" />
                )}
              </button>
              <div
                className={classNames(
                  isLiked ? "text-primary" : "text-secondary",
                )}
              >
                {likesCount}
              </div>
            </div>
            <div className={styles.createdAtPostDate}>
              {formatWithLocale(new Date(createdAt), postDateFormat)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Comment;
